import type {
  CampaignLaunchBlockedPost,
  CampaignLaunchBlockReason,
  CampaignLaunchQuestion,
  CampaignLaunchResponse,
} from 'fym-shared/src/music24/campaignLaunch';
import type { PlanStepView } from '../rolloutModel';

/**
 * The pure rules of "Schedule all N posts" in the run sheet (owner report 2026-10-10; fym-shared
 * `campaignLaunch.ts`). The server decides what is scheduled; this file turns its answer into the
 * summary "18 scheduled. 4 need you:" with one fix for each post.
 */

/** The one button of a post that was not scheduled. */
export type LaunchFix = 'reconnect' | 'openPost' | 'plans' | 'answer';

const FIX_OF_REASON: Record<CampaignLaunchBlockReason, LaunchFix> = {
  needsAnswer: 'answer',
  reconnect: 'reconnect',
  mediaMissing: 'openPost',
  mediaRefused: 'openPost',
  timePast: 'openPost',
  monthLimit: 'plans',
  channelLimit: 'plans',
  tiktokWatermark: 'plans',
  // The X post stays a draft: the artist posts it by hand or gives it a time next month.
  xMonthLimit: 'openPost',
};

export function launchFixOf(reason: CampaignLaunchBlockReason): LaunchFix {
  return FIX_OF_REASON[reason];
}

/** What a launch did, kept on the page until the next launch or a dismiss. */
export interface LaunchOutcome {
  /**
   * The place that started it: `plan` is "Schedule all" of the run sheet, else a step, the "Needs
   * you" list or `day` (the Calendar).
   */
  place: string;
  /** The posts that are scheduled now (this call and a call before it). */
  scheduled: number;
  /**
   * The uuids of those posts: the summary splits them into posts that go out by themselves and
   * reminders. Absent: not known.
   */
  scheduledUuids?: string[];
  needsYou: CampaignLaunchBlockedPost[];
}

export function launchOutcomeOf(place: string, answer: CampaignLaunchResponse): LaunchOutcome {
  return {
    place,
    scheduled: answer.scheduled.length + answer.alreadyScheduled.length,
    scheduledUuids: [...answer.scheduled, ...answer.alreadyScheduled],
    needsYou: answer.needsYou,
  };
}

/** The questions that the dialog asks, and the posts that a launch with every answer schedules. */
export interface PendingLaunch {
  place: string;
  postUuids: string[];
  questions: CampaignLaunchQuestion[];
  /** The posts that go out when every question has its answer. */
  postCount: number;
}

export function pendingLaunchOf(place: string, postUuids: string[], dry: CampaignLaunchResponse): PendingLaunch {
  return {
    place,
    postUuids,
    questions: dry.questions,
    postCount: dry.scheduled.length + dry.questions.reduce((sum, question) => sum + question.postCount, 0),
  };
}

/** One row of the summary: the post by its step and channel, the reason and the fix. */
export interface LaunchRow {
  postUuid: string;
  /** Null when the post is not a step of this plan now (the plan changed meanwhile). */
  step: PlanStepView | null;
  platform: string;
  reason: CampaignLaunchBlockReason;
  detail: string | null;
  fix: LaunchFix;
}

/** One row as the shared summary shows it: the screen gives the name of the post. */
export interface LaunchSummaryRow {
  postUuid: string;
  /** The name of the post on this screen: "Countdown · YouTube" (plan), "Fri 16 Oct, 18:00 · YouTube" (Calendar). */
  title: string;
  platform: string;
  reason: CampaignLaunchBlockReason;
  detail: string | null;
  fix: LaunchFix;
}

/** The rows of the summary, in the order of the plan. */
export function launchRows(
  needsYou: readonly CampaignLaunchBlockedPost[],
  steps: readonly PlanStepView[]
): LaunchRow[] {
  const stepOfPost = new Map<string, { step: PlanStepView; order: number }>();
  steps.forEach((step, order) => {
    step.channels.forEach((channel) => {
      if (channel.post !== null) {
        stepOfPost.set(channel.post.uuid, { step, order });
      }
    });
  });
  return needsYou
    .map((one, index) => ({ one, index, found: stepOfPost.get(one.postUuid) }))
    .sort((a, b) => {
      // A post that is not a step of the plan now goes last; the server order breaks a tie.
      const byStep = (a.found?.order ?? steps.length) - (b.found?.order ?? steps.length);
      return byStep !== 0 ? byStep : a.index - b.index;
    })
    .map(({ one, found }) => ({
      postUuid: one.postUuid,
      step: found?.step ?? null,
      platform: one.platform,
      reason: one.reason,
      detail: one.detail,
      fix: launchFixOf(one.reason),
    }));
}

/** The headline of the summary: all scheduled, a part scheduled, or nothing scheduled. */
export type LaunchHeadline = 'allScheduled' | 'partScheduled' | 'noneScheduled' | 'none';

/** The place name of "Schedule all drafts of this day" in the Calendar. */
export const CALENDAR_LAUNCH_PLACE = 'day';

const ALL_SCHEDULED_PLACES: readonly string[] = ['plan', CALENDAR_LAUNCH_PLACE];

export function launchHeadline(outcome: LaunchOutcome): LaunchHeadline {
  if (outcome.needsYou.length === 0) {
    // One step that is scheduled says so on its own card; only "Schedule all" of the plan and of
    // a Calendar day gets the headline.
    return ALL_SCHEDULED_PLACES.includes(outcome.place) && outcome.scheduled > 0 ? 'allScheduled' : 'none';
  }
  return outcome.scheduled > 0 ? 'partScheduled' : 'noneScheduled';
}

/**
 * The posts that the last launch left as drafts because this month has no post left (`monthLimit`).
 * The limit upsell card schedules them again after an add-on (design limit-upsell a11). Pure.
 */
export function monthLimitPosts(outcome: LaunchOutcome | null): string[] {
  if (outcome === null) {
    return [];
  }
  return outcome.needsYou.filter((post) => post.reason === 'monthLimit').map((post) => post.postUuid);
}

/**
 * "Schedule only what fits" (design limit-upsell a1): the first `left` drafts in time order, the
 * same order in which the server gives the units of the month (`launchPromoDrafts`). Pure.
 */
export function draftsThatFitNow(drafts: ReadonlyArray<{ uuid: string; scheduledAt: string }>, left: number): string[] {
  return [...drafts]
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt))
    .slice(0, Math.max(0, left))
    .map((draft) => draft.uuid);
}
